/**
 * Product media gallery enhancements:
 *
 * 1. Instagram-style dynamic pagination dots on the mobile gallery
 *    (`.media-gallery__mobile-controls`): a sliding window of dots, edge dots
 *    shrink, inactive dots render smaller than the active one, and the strip
 *    supports press-and-drag scrubbing (with vibration where the platform
 *    supports it — Android; iOS Safari has no vibration API).
 *
 * 2. Scroll fade on the desktop thumbnail strip: when more thumbnails exist
 *    beyond the visible edge, the strip fades out on that side as a cue.
 *
 * Relies on slideshow.js keeping `aria-selected` up to date on dots/thumbnails.
 */
const MAX_VISIBLE = 5;

class InstagramDots {
  /** @param {HTMLElement} controls - a <slideshow-controls> element */
  constructor(controls) {
    this.list = controls.querySelector('.slideshow-controls__dots');
    if (!this.list) return;

    this.items = Array.from(this.list.children);
    this.dots = this.items.map((li) => li.querySelector('.slideshow-control'));
    if (this.items.length <= MAX_VISIBLE) return;

    this.list.classList.add('ig-dots');
    this.measure();
    this.update();

    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this.list, {
      attributes: true,
      attributeFilter: ['aria-selected'],
      subtree: true,
    });

    this.resizeObserver = new ResizeObserver(() => {
      // rAF-deferred: layout writes inside a ResizeObserver callback retrigger
      // the observer in the same frame and throw the benign-but-Clarity-counted
      // "ResizeObserver loop" error (32% of .com sessions, 2026-07-21 report).
      requestAnimationFrame(() => {
        this.measure();
        this.update();
      });
    });
    this.resizeObserver.observe(this.list);

    // Re-measure when the desktop/mobile breakpoint flips — the dots strip
    // toggles between hidden (thumbnails shown) and visible there.
    this.breakpoint = matchMedia('(max-width: 749px)');
    this.breakpoint.addEventListener('change', () => {
      this.measure();
      this.update();
    });

    this.setupScrub();
  }

  refreshItems() {
    // The dot list is NOT static: variant media filtering and section
    // morphs add/remove/replace <li>s after construction. A stale snapshot
    // froze the strip once the active dot moved past the known set
    // (activeIndex() = -1), while the plain dots kept tracking the swipe.
    this.items = Array.from(this.list.children);
    this.dots = this.items.map((li) => li.querySelector('.slideshow-control'));
  }

  measure() {
    this.refreshItems();
    const [first, second] = this.items;
    if (!first || !second) return;
    // While hidden (e.g. the mobile controls on a desktop viewport) all
    // offsets read 0 — never apply measurements taken in that state, or the
    // strip stays clipped to nothing when it becomes visible.
    if (!first.offsetWidth) {
      this.step = 0;
      this.list.style.removeProperty('max-width');
      return;
    }
    this.step = second.offsetLeft - first.offsetLeft || first.offsetWidth;
    const gap = this.step - first.offsetWidth;
    // Padding (14px each side in CSS) gives the enlarged active dot room
    // at the window edges.
    this.list.style.maxWidth = `${this.step * MAX_VISIBLE - gap + 28}px`;
  }

  activeIndex() {
    return this.dots.findIndex((b) => b?.getAttribute('aria-selected') === 'true');
  }

  update() {
    if (!this.step) return; // not measurable yet (hidden) — leave dots natural
    this.refreshItems();
    const count = this.items.length;
    if (count <= MAX_VISIBLE) {
      // gallery shrank below the windowing threshold — show natural dots
      this.list.style.removeProperty('--ig-offset');
      this.items.forEach((li) => {
        li.style.removeProperty('--dot-scale');
        li.classList.remove('ig-dot-hidden');
      });
      return;
    }
    const active = this.activeIndex();
    if (active === -1) return;

    const windowStart = Math.min(Math.max(active - 2, 0), count - MAX_VISIBLE);
    const windowEnd = windowStart + MAX_VISIBLE - 1;

    this.list.style.setProperty('--ig-offset', `${-windowStart * this.step}px`);

    // Instagram-style progressive sizing: the active dot is largest and
    // dots taper down the farther they sit from it.
    this.items.forEach((li, i) => {
      const distance = Math.abs(i - active);
      const outside = i < windowStart || i > windowEnd;
      const scale = distance === 0 ? 1.08 : Math.max(0.95 - distance * 0.2, 0.38);
      li.style.setProperty('--dot-scale', outside ? 0.35 : scale.toFixed(2));
      li.classList.toggle('ig-dot-hidden', outside);
    });
  }

  goTo(index) {
    const target = Math.min(Math.max(index, 0), this.dots.length - 1);
    if (target === this.lastScrubIndex) return;
    this.lastScrubIndex = target;
    this.dots[target]?.click();
    navigator.vibrate?.(5);
  }

  /**
   * Press anywhere on the dots strip and drag horizontally to scrub through
   * slides, Instagram-style. Each `step` of horizontal travel moves one
   * slide; releasing mid-swipe carries iOS-like momentum that decays.
   */
  setupScrub() {
    let startX = 0;
    let startIndex = 0;
    let scrubbing = false;
    let samples = [];

    const stopMomentum = () => cancelAnimationFrame(this.momentumFrame);

    this.list.addEventListener('pointerdown', (e) => {
      stopMomentum();
      scrubbing = true;
      startX = e.clientX;
      startIndex = this.activeIndex();
      this.lastScrubIndex = startIndex;
      samples = [{ x: e.clientX, t: e.timeStamp }];
      this.list.setPointerCapture(e.pointerId);
    });

    this.list.addEventListener('pointermove', (e) => {
      if (!scrubbing) return;
      e.preventDefault();
      samples.push({ x: e.clientX, t: e.timeStamp });
      if (samples.length > 5) samples.shift();
      this.goTo(startIndex + Math.round((e.clientX - startX) / this.step));
    });

    const release = (e) => {
      if (!scrubbing) return;
      scrubbing = false;

      // Momentum: velocity from the last few pointer samples, decaying ~6%
      // per frame, keeps advancing the virtual position after release.
      const first = samples[0];
      const last = samples[samples.length - 1];
      const dt = last && first ? last.t - first.t : 0;
      let velocity = dt > 0 ? (last.x - first.x) / dt : 0; // px per ms
      if (Math.abs(velocity) < 0.15) return;

      let virtualX = e.clientX - startX;
      let prev = performance.now();
      const glide = (now) => {
        const elapsed = now - prev;
        prev = now;
        virtualX += velocity * elapsed;
        velocity *= Math.pow(0.94, elapsed / 16.7);
        this.goTo(startIndex + Math.round(virtualX / this.step));
        const atBound =
          this.lastScrubIndex === 0 || this.lastScrubIndex === this.dots.length - 1;
        if (Math.abs(velocity) > 0.02 && !atBound) {
          this.momentumFrame = requestAnimationFrame(glide);
        }
      };
      this.momentumFrame = requestAnimationFrame(glide);
    };

    this.list.addEventListener('pointerup', release);
    this.list.addEventListener('pointercancel', () => (scrubbing = false));
  }

  destroy() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }
}

/**
 * Keeps the desktop thumbnail selection (black outline) in sync with the
 * photo actually being displayed. The theme's own sync path can go stale
 * when a colour-swatch click swaps the gallery content in place (combined
 * listings): the slides are replaced and the scroll position persists, but
 * the rebuilt thumbnails default their selection to the first thumb. This
 * observes the slides directly and corrects the outline whenever the
 * visible slide changes, regardless of what caused the change.
 */
class ThumbnailSelectionSync {
  /** @param {HTMLElement} gallery - a <media-gallery> element */
  constructor(gallery) {
    this.gallery = gallery;

    // Watch the whole gallery: slide visibility changes (arrows/swipes) AND
    // subtree rebuilds (a colour-swatch swap replaces the thumbnails with
    // stale selection while the slides keep their old state — the sync must
    // fire on landing, not only on the next photo change).
    // The moment a colour swatch is picked — BEFORE the new colour's content
    // arrives — reset to photo 1 and dim the gallery as instant feedback.
    // The `change` event fires reliably however the swatch is activated
    // (clicking the circle, the label, or via keyboard).
    this.onSwatchChange = (e) => {
      if (!e.target?.dataset?.connectedProductUrl) return;
      this.resetToFirstPhoto();
      this.gallery.classList.add('gallery-loading');
      clearTimeout(this.loadingTimeout);
      // safety: never stay dimmed if the swap fails
      this.loadingTimeout = setTimeout(() => this.gallery.classList.remove('gallery-loading'), 4000);
    };
    document.addEventListener('change', this.onSwatchChange, true);

    this.mediaFingerprint = this.fingerprint();
    this.observer = new MutationObserver(() => {
      // A colour-swatch swap replaces the gallery's PHOTOS (different media
      // ids) but the scroller silently keeps its old physical position,
      // leaving the fresh state (photo 1 marked visible, outline on thumb 1)
      // out of step with what is shown. When the media set changes, start
      // the new colour at its first photo — this runs before the browser
      // paints, so there is no visible jump or outline bounce. Normal
      // browsing (arrows, swipes, thumb clicks) keeps the same media set and
      // is never reset.
      const fp = this.fingerprint();
      if (fp !== this.mediaFingerprint) {
        this.mediaFingerprint = fp;
        this.resetToFirstPhoto();
        this.undimWhenPhotoReady();
        reinitAppWidgets();
        refreshLazyCompare();
      }
      this.requestSync();
    });
    this.observer.observe(gallery, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
    this.requestSync();
  }

  /** Identity of the current media set (changes only when the colour changes).
      Keyed on the FIRST slide's image URL only: every colour has a distinct
      first photo, while slide count and tail slides churn during normal
      browsing (e.g. video slides instantiate lazily) and must not count. */
  fingerprint() {
    const img = this.gallery.querySelector('slideshow-component slideshow-slide img');
    return img?.getAttribute('src') || '';
  }

  resetToFirstPhoto() {
    const scroller = this.gallery.querySelector('slideshow-component slideshow-slides');
    if (scroller && scroller.scrollLeft !== 0) {
      scroller.scrollTo({ left: 0, behavior: 'instant' });
    }
  }

  /** Lift the loading dim only once the new colour's first photo has actually
      downloaded — so the dim reveals the NEW photo, never a stale frame of
      the old one. Polls the CURRENT first image (the swap can replace the
      element itself, which makes one-shot load listeners unreliable). */
  undimWhenPhotoReady() {
    clearInterval(this.undimPoll);
    const check = () => {
      const img = this.gallery.querySelector('slideshow-component slideshow-slide img');
      if (img && !(img.complete && img.naturalWidth > 0)) return; // still loading
      clearInterval(this.undimPoll);
      this.gallery.classList.remove('gallery-loading');
      clearTimeout(this.loadingTimeout);
    };
    this.undimPoll = setInterval(check, 80);
    check();
  }

  requestSync() {
    // coalesce mutation bursts (morphs fire many) into one sync per frame
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.sync();
    });
  }

  sync() {
    // query fresh every time — swaps can replace these elements entirely
    const slides = [...this.gallery.querySelectorAll('slideshow-component slideshow-slide')];
    const visible = slides.findIndex((s) => s.getAttribute('aria-hidden') === 'false');
    if (visible === -1) return;
    const thumbs = [
      ...this.gallery.querySelectorAll('slideshow-controls[thumbnails] .slideshow-controls__thumbnail'),
    ];
    if (!thumbs.length) return;

    let changed = false;
    thumbs.forEach((t, i) => {
      const want = `${i === visible}`;
      // only write when different — avoids observer feedback loops
      if (t.getAttribute('aria-selected') !== want) {
        t.setAttribute('aria-selected', want);
        changed = true;
      }
    });
    if (changed) {
      thumbs[visible]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }

  destroy() {
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    clearTimeout(this.loadingTimeout);
    clearInterval(this.undimPoll);
    document.removeEventListener('change', this.onSwatchChange, true);
  }
}

/**
 * Instagram-style dots for Swiper-based carousels (the custom "gallery",
 * "materials", product-list sections etc.). Same behaviour as the product
 * gallery's mobile dots: a sliding 5-dot window with distance-based sizing
 * and press-and-drag scrubbing, applied only when a carousel has 6+ dots —
 * shorter carousels keep their uniform dots, matching Instagram.
 */
class SwiperIgDots {
  /** @param {HTMLElement} pagination - a .swiper-pagination element */
  constructor(pagination) {
    this.list = pagination;
    this.items = [...pagination.querySelectorAll('.swiper-pagination-bullet')];
    if (this.items.length <= MAX_VISIBLE) return;

    pagination.classList.add('ig-swiper-dots');
    this.enhanced = true;
    this.measure();
    this.update();

    this.observer = new MutationObserver(() => this.requestUpdate());
    this.observer.observe(pagination, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.measure();
        this.requestUpdate();
      });
    });
    this.resizeObserver.observe(pagination);
    this.setupScrub();
  }

  requestUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  refreshItems() {
    // Swiper rebuilds its bullets on re-init/morph — never trust the
    // construction-time snapshot (same staleness bug as InstagramDots).
    this.items = [...this.list.querySelectorAll('.swiper-pagination-bullet')];
  }

  measure() {
    this.refreshItems();
    const [first, second] = this.items;
    if (!first || !second || !first.offsetWidth) {
      this.step = 0;
      this.list.style.removeProperty('max-width');
      return;
    }
    this.step = second.offsetLeft - first.offsetLeft || first.offsetWidth;
    const gap = this.step - first.offsetWidth;
    // The strip keeps its container's own padding (e.g. the swiper-control
    // pill), so include the real computed value in the window width.
    const cs = getComputedStyle(this.list);
    const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    this.list.style.maxWidth = `${this.step * MAX_VISIBLE - gap + pad}px`;
  }

  activeIndex() {
    return this.items.findIndex((b) => b.classList.contains('swiper-pagination-bullet-active'));
  }

  update() {
    if (!this.step) return;
    this.refreshItems();
    const count = this.items.length;
    if (count <= MAX_VISIBLE) {
      this.list.style.removeProperty('--ig-offset');
      this.items.forEach((b) => {
        b.style.removeProperty('--dot-scale');
        b.classList.remove('ig-dot-hidden');
      });
      return;
    }
    const active = this.activeIndex();
    if (active === -1) return;

    const windowStart = Math.min(Math.max(active - 2, 0), count - MAX_VISIBLE);
    const windowEnd = windowStart + MAX_VISIBLE - 1;

    this.list.style.setProperty('--ig-offset', `${-windowStart * this.step}px`);

    this.items.forEach((b, i) => {
      const distance = Math.abs(i - active);
      const outside = i < windowStart || i > windowEnd;
      const scale = distance === 0 ? 1.08 : Math.max(0.95 - distance * 0.2, 0.38);
      const want = outside ? '0.35' : scale.toFixed(2);
      if (b.style.getPropertyValue('--dot-scale') !== want) b.style.setProperty('--dot-scale', want);
      b.classList.toggle('ig-dot-hidden', outside);
    });
  }

  goTo(index) {
    const target = Math.min(Math.max(index, 0), this.items.length - 1);
    if (target === this.lastScrubIndex) return;
    this.lastScrubIndex = target;
    this.items[target]?.click();
    navigator.vibrate?.(5);
  }

  setupScrub() {
    let startX = 0;
    let startIndex = 0;
    let scrubbing = false;
    let samples = [];
    const stopMomentum = () => cancelAnimationFrame(this.momentumFrame);

    this.list.addEventListener('pointerdown', (e) => {
      stopMomentum();
      scrubbing = true;
      startX = e.clientX;
      startIndex = this.activeIndex();
      this.lastScrubIndex = startIndex;
      samples = [{ x: e.clientX, t: e.timeStamp }];
      this.list.setPointerCapture(e.pointerId);
    });

    this.list.addEventListener('pointermove', (e) => {
      if (!scrubbing) return;
      e.preventDefault();
      samples.push({ x: e.clientX, t: e.timeStamp });
      if (samples.length > 5) samples.shift();
      this.goTo(startIndex + Math.round((e.clientX - startX) / this.step));
    });

    const release = (e) => {
      if (!scrubbing) return;
      scrubbing = false;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const dt = last && first ? last.t - first.t : 0;
      let velocity = dt > 0 ? (last.x - first.x) / dt : 0;
      if (Math.abs(velocity) < 0.15) return;
      let virtualX = e.clientX - startX;
      let prev = performance.now();
      const glide = (now) => {
        const elapsed = now - prev;
        prev = now;
        virtualX += velocity * elapsed;
        velocity *= Math.pow(0.94, elapsed / 16.7);
        this.goTo(startIndex + Math.round(virtualX / this.step));
        const atBound = this.lastScrubIndex === 0 || this.lastScrubIndex === this.items.length - 1;
        if (Math.abs(velocity) > 0.02 && !atBound) this.momentumFrame = requestAnimationFrame(glide);
      };
      this.momentumFrame = requestAnimationFrame(glide);
    };
    this.list.addEventListener('pointerup', release);
    this.list.addEventListener('pointercancel', () => (scrubbing = false));
  }

  destroy() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.momentumFrame);
  }
}

/**
 * Lazy "Compare Backpacks" popup: product pages used to pre-render the full
 * comparison (a product card per backpack in the compare collection) on
 * every page view. The popup shell now fetches that content the first time
 * it's needed — warmed on hover so the click feels instant.
 */
function setupLazyCompare() {
  const lazy = document.querySelector('.compare-popup-wrapper .compare-lazy');
  if (!lazy || lazy.dataset.wired) return;
  lazy.dataset.wired = '1';
  lazy.dataset.state = 'idle';

  const load = async () => {
    if (lazy.dataset.state !== 'idle') return;
    lazy.dataset.state = 'loading';
    lazy.querySelector('.compare-lazy__loading')?.removeAttribute('hidden');
    try {
      const res = await fetch(lazy.dataset.compareSectionUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const section = doc.querySelector('.shopify-section') || doc.body;
      lazy.innerHTML = section.innerHTML;
      lazy.dataset.state = 'loaded';
      // the injected inline Swiper <script> is inert; the init() sweep
      // re-executes it via the same clone mechanism used for colour swaps
    } catch (e) {
      lazy.dataset.state = 'idle';
      lazy.querySelector('.compare-lazy__loading')?.setAttribute('hidden', '');
    }
  };

  document.querySelectorAll('.button-compare').forEach((btn) => {
    btn.addEventListener('pointerenter', load);
    btn.addEventListener('click', load);
  });
}

/**
 * Instant feedback on variant picks. The server round-trip that re-renders
 * the picker (and, for combined listings, the whole page) takes 1–3s — but
 * every click should visibly do something within a frame. So on any radio
 * pick inside a variant picker:
 *   - the row's selected-value label ("Color: Ripstop Black") is painted
 *     immediately with the clicked value (the server render then confirms it)
 *   - dependent rows built from the selection (the bundle preview tiles,
 *     the "Fits" row) dim until their fresh content lands — the incoming
 *     server HTML carries no `is-refreshing` class, which lifts the dim.
 */
document.addEventListener(
  'change',
  (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const picker = input.closest('variant-picker');
    if (!picker) return;

    input
      .closest('fieldset')
      ?.querySelectorAll('.variant-option__swatch-value')
      .forEach((span) => (span.textContent = input.value));

    picker.querySelectorAll('.bundle-preview, .fits-packs').forEach((el) => {
      el.classList.add('is-refreshing');
      clearTimeout(el.refreshingTimeout);
      // safety: never stay dimmed if the update fails to arrive
      el.refreshingTimeout = setTimeout(() => el.classList.remove('is-refreshing'), 4000);
    });
  },
  true
);

/** After a colour swap the popup should compare against the NEW product. */
function refreshLazyCompare() {
  const lazy = document.querySelector('.compare-popup-wrapper .compare-lazy');
  if (!lazy || lazy.dataset.state !== 'loaded') return;
  lazy.dataset.compareSectionUrl = `${location.pathname}?section_id=compare-drawer`;
  lazy.innerHTML = '<p class="compare-lazy__loading" hidden>Loading comparison…</p>';
  lazy.dataset.state = 'idle';
}

/**
 * App widgets (e.g. Okendo reviews) live inside sections that a
 * colour-variant swap re-renders — but the apps' scripts don't know a swap
 * happened, leaving half-built widgets that can overflow the viewport.
 * Ask them to re-initialize, with retries because their APIs load lazily.
 */
function reinitAppWidgets(attempt = 0) {
  try {
    window.okeWidgetApi?.initAllWidgets?.();
  } catch (e) {
    /* widget re-init must never break the page */
  }
  if (attempt < 3) setTimeout(() => reinitAppWidgets(attempt + 1), 1500);
}

/** Fades the thumbnail strip edges when it can scroll further. */
class ThumbnailScrollFade {
  /** @param {HTMLElement} container - the .slideshow-controls__thumbnails-container */
  constructor(container) {
    this.container = container;
    this.update = this.update.bind(this);
    container.addEventListener('scroll', this.update, { passive: true });
    this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(this.update));
    this.resizeObserver.observe(container);
    this.update();
  }

  update() {
    const el = this.container;
    const max = el.scrollWidth - el.clientWidth;
    el.classList.toggle('thumbs-fade-left', max > 1 && el.scrollLeft > 4);
    el.classList.toggle('thumbs-fade-right', max > 1 && el.scrollLeft < max - 4);
  }

  destroy() {
    this.resizeObserver?.disconnect();
    this.container.removeEventListener('scroll', this.update);
  }
}

function init() {
  document
    .querySelectorAll('slideshow-controls.media-gallery__mobile-controls')
    .forEach((controls) => {
      // Re-enhance when a colour swap disturbed the enhancement — the swap
      // can replace the list, replace the dot items inside it, or keep the
      // element but STRIP the ig-dots class (attribute sync against server
      // HTML). Unenhanced 6+ dot strips stay hidden by the anti-flash CSS,
      // so any of these must trigger a rebuild.
      const inst = controls.igDots;
      const healthy =
        inst?.list?.isConnected &&
        (inst.items?.[0] ? inst.items[0].isConnected : true) &&
        (inst.items?.length <= 5 || inst.list.classList.contains('ig-dots'));
      if (inst && healthy) return;
      inst?.destroy?.();
      controls.igDots = new InstagramDots(controls);
    });

  document
    .querySelectorAll('slideshow-controls[thumbnails] .slideshow-controls__thumbnails-container')
    .forEach((container) => {
      if (container.thumbFade) {
        container.thumbFade.update(); // re-apply fade classes a swap stripped
        return;
      }
      container.thumbFade = new ThumbnailScrollFade(container);
    });

  document.querySelectorAll('media-gallery').forEach((gallery) => {
    if (gallery.thumbSync) return; // observer re-queries fresh; survives swaps
    gallery.thumbSync = new ThumbnailSelectionSync(gallery);
  });

  // A colour-variant page swap rebuilds Swiper sections but their inline
  // init scripts come back inert (scripts inserted by a DOM morph don't
  // execute) — the same applies to lazily-injected content like the compare
  // popup. Re-run each container's own init by cloning its script — only
  // for carousels that are actually uninitialized, once per script.
  document.querySelectorAll('.swiper:not(.swiper-initialized)').forEach((el) => {
    const section = el.closest('.shopify-section, .compare-lazy, dialog') || el.parentElement;
    if (!section) return;
    section.querySelectorAll('script:not([src])').forEach((sc) => {
      if (sc.dataset.swiperReinit || !/new Swiper/.test(sc.textContent)) return;
      sc.dataset.swiperReinit = '1';
      const clone = document.createElement('script');
      clone.dataset.swiperReinit = '1';
      clone.textContent = sc.textContent;
      sc.after(clone);
    });
  });

  // Instagram-style dots for all Swiper carousels site-wide. (Note: the
  // pagination element often sits OUTSIDE the .swiper container in this
  // theme, so match paginations directly — bullets only exist post-init.)
  document.querySelectorAll('.swiper-pagination').forEach((p) => {
    const inst = p.igSwiperDots;
    const bullets = p.querySelectorAll('.swiper-pagination-bullet');
    const healthy =
      inst &&
      inst.items?.[0]?.isConnected &&
      inst.items.length === bullets.length &&
      (inst.items.length <= 5 || p.classList.contains('ig-swiper-dots'));
    if (healthy) return;
    inst?.destroy?.();
    p.igSwiperDots = new SwiperIgDots(p);
  });

  setupLazyCompare();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-init after section re-renders (variant changes can rebuild the gallery)
new MutationObserver(init).observe(document.body, { childList: true, subtree: true });
